import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Search, Sparkles, Bell, Settings, LogOut, ChevronDown } from 'lucide-react';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

interface TopbarProps {
  title: string;
  breadcrumb: string[];
  user: { name?: string; role?: string } | null;
  onOpenMobileMenu: () => void;
  onOpenAi?: () => void;
  onLogout: () => void;
  notifications?: number;
  onOpenNotifications?: () => void;
}

export function Topbar({
  title,
  breadcrumb,
  user,
  onOpenMobileMenu,
  onOpenAi,
  onLogout,
  notifications = 0,
  onOpenNotifications,
}: TopbarProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Atalho Ctrl+/ foca busca
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Fecha o menu do usuário ao clicar fora
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <header className="h-14 flex-shrink-0 flex items-center gap-3 px-4 lg:px-5 border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark sticky top-0 z-20">
      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={onOpenMobileMenu}
        className="lg:hidden p-2 -ml-2 rounded-lg text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt hover:text-brand-600 transition-colors"
        aria-label="Abrir menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Breadcrumb + title */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        {breadcrumb.length > 0 && (
          <div className="hidden md:flex items-center gap-1.5 text-[12px] leading-none mb-0.5">
            {breadcrumb.map((b, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span className="text-ink-placeholder dark:text-ink-placeholder-dark">/</span>
                )}
                <span
                  className={
                    i === breadcrumb.length - 1
                      ? 'text-ink-title dark:text-ink-title-dark font-semibold'
                      : 'text-ink-subtitle dark:text-ink-subtitle-dark'
                  }
                >
                  {b}
                </span>
              </React.Fragment>
            ))}
          </div>
        )}
        <h1 className="text-[15px] font-semibold text-ink-title dark:text-ink-title-dark tracking-[-0.01em] truncate md:hidden">
          {title}
        </h1>
      </div>

      {/* Search — centro */}
      <div className="hidden lg:flex items-center gap-2 w-[360px] h-[34px] px-2.5 rounded-lg bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark focus-within:border-brand-500 dark:focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-500/20 transition-all">
        <Search className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark flex-shrink-0" />
        <input
          ref={searchRef}
          type="text"
          placeholder="Buscar editais, empenhos, contratos…"
          className="flex-1 bg-transparent border-none outline-none text-[13px] text-ink-title dark:text-ink-title-dark placeholder:text-ink-placeholder dark:placeholder:text-ink-placeholder-dark font-[inherit]"
        />
        <kbd className="hidden xl:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10.5px] font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded">
          Ctrl
          <span className="opacity-60">/</span>
        </kbd>
      </div>

      {/* Ações direita */}
      <div className="flex items-center gap-1.5 md:gap-2 ml-auto">
        {/* Assistente IA */}
        <button
          type="button"
          onClick={onOpenAi}
          className="hidden md:inline-flex items-center gap-1.5 h-[34px] px-3 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-medium transition-colors shadow-sm-warm"
        >
          <Sparkles className="w-4 h-4" />
          <span className="hidden lg:inline">Assistente IA</span>
          <kbd className="hidden lg:inline-flex items-center px-1 py-[1px] text-[10.5px] font-medium bg-white/20 rounded">
            ⌘K
          </kbd>
        </button>

        {/* Theme toggle */}
        <ThemeToggle />

        {/* Notifications */}
        <button
          type="button"
          onClick={onOpenNotifications}
          className="relative w-[34px] h-[34px] inline-flex items-center justify-center rounded-lg text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt hover:text-ink-text dark:hover:text-ink-text-dark transition-colors"
          title={notifications > 0 ? `${notifications} alerta(s) de chat pendente(s)` : 'Notificações'}
          aria-label={notifications > 0 ? `Abrir ${notifications} alertas de chat pendentes` : 'Abrir notificações'}
        >
          <Bell className="w-4 h-4" />
          {notifications > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 inline-flex items-center justify-center text-[10px] font-semibold text-white bg-brand-500 rounded-full border-2 border-surface dark:border-surface-dark">
              {notifications > 9 ? '9+' : notifications}
            </span>
          )}
        </button>

        {/* Avatar com dropdown: usuário + Configurações + Sair */}
        {user && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              className="flex items-center gap-1 pr-1 rounded-full hover:bg-surface-alt dark:hover:bg-surface-dark-alt transition-colors"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title={`${user.name || ''}${user.role ? ' · ' + user.role : ''}`}
            >
              <div className="w-8 h-8 bg-brand-500 text-white rounded-full flex items-center justify-center text-[13px] font-semibold ring-2 ring-surface dark:ring-surface-dark flex-shrink-0">
                {user.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-ink-subtitle dark:text-ink-subtitle-dark" />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1.5 w-56 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg shadow-lg-warm dark:shadow-lg-dark overflow-hidden z-30"
              >
                {/* Header do menu: nome + role */}
                <div className="px-3 py-2.5 border-b border-line dark:border-line-dark">
                  <div className="text-[13px] font-semibold text-ink-title dark:text-ink-title-dark truncate">
                    {user.name || 'Usuário'}
                  </div>
                  <div className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark truncate">
                    {user.role || ''}
                  </div>
                </div>

                {/* Itens */}
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-[13px] text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt transition-colors"
                >
                  <Settings className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                  Configurações
                </Link>

                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onLogout(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-danger dark:text-danger-dark hover:bg-danger-soft dark:hover:bg-danger-soft-dark transition-colors border-t border-line dark:border-line-dark"
                >
                  <LogOut className="w-4 h-4" />
                  Sair
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
